import type { Meta, StoryObj } from '@storybook/angular';
import { applicationConfig, moduleMetadata } from '@storybook/angular';
import { provideHttpClient } from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { expect } from 'storybook/test';

import { StoryblokCheckPostcodeComponent } from '../../app/content/modules/storyblok/components/storyblok-check-postcode/storyblok-check-postcode.component';
import { DeliveryService } from '../../app/core/services/delivery.service';

// Mock service for testing
class MockDeliveryService {
  private mockResponse: { isDeliverable: boolean; errors?: string[] } | null = null;
  private shouldFail = false;

  setMockResponse(response: { isDeliverable: boolean; errors?: string[] }) {
    this.mockResponse = response;
    this.shouldFail = false;
  }

  setShouldFail(fail: boolean) {
    this.shouldFail = fail;
  }

  async isPostcodeDeliverable(postcode: string, countryCode: string): Promise<{ isDeliverable: boolean; errors?: string[] }> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (this.shouldFail) {
      throw new Error('Network error');
    }

    if (this.mockResponse) {
      return this.mockResponse;
    }

    // Default behavior: deliverable for postcodes starting with 1, not deliverable for others
    // Note: postcode will be without spaces as the component removes them before calling the service
    return {
      isDeliverable: postcode.startsWith('1'),
      errors: postcode.startsWith('1') ? [] : ['Delivery not available']
    };
  }
}

const meta: Meta<StoryblokCheckPostcodeComponent> = {
  title: 'Molecules/Check Postcode',
  component: StoryblokCheckPostcodeComponent,
  tags: ['autodocs'],
  decorators: [
    moduleMetadata({
      imports: [ReactiveFormsModule, StoryblokCheckPostcodeComponent],
      providers: [
        { provide: DeliveryService, useClass: MockDeliveryService }
      ],
    }),
    applicationConfig({
      providers: [provideHttpClient()],
    }),
  ],
  render: (args) => ({
    props: args,
  }),
};

export default meta;
type Story = StoryObj<StoryblokCheckPostcodeComponent>;

export const Default: Story = {
  render: () => ({
    template: `
      <div style="max-width: 600px; padding: 2rem;">
        <h3 style="margin-bottom: 1rem;">Check Postcode Component</h3>
        <app-check-postcode />
        <div style="margin-top: 2rem; padding: 1rem; background: #f5f5f5; border-radius: 4px;">
          <h4 style="margin-top: 0;">Test Instructions:</h4>
          <ul style="margin-bottom: 0;">
            <li><strong>Deliverable:</strong> Enter postcode starting with "1" (e.g., 10000, 12345, 123 45)</li>
            <li><strong>Not Deliverable:</strong> Enter postcode starting with other digits (e.g., 20000, 987 65)</li>
            <li><strong>Format:</strong> Swedish postcodes can be entered with or without space (12345 or 123 45)</li>
            <li><strong>Validation Error:</strong> Enter incorrect format (e.g., 123, 1234a)</li>
          </ul>
        </div>
      </div>
    `,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-check-postcode')).toBeTruthy();
  },
};

export const SuccessState: Story = {
  name: 'Success - Deliverable Postcode',
  render: () => ({
    template: `
      <div style="max-width: 600px; padding: 2rem;">
        <h3 style="margin-bottom: 1rem;">Success State</h3>
        <app-check-postcode />
        <div style="margin-top: 2rem; padding: 1rem; background: #e8f5e9; border-radius: 4px;">
          <p style="margin: 0;"><strong>Mock:</strong> Returns deliverable = true</p>
          <p style="margin: 0.5rem 0 0 0;">Enter a postcode with or without space (e.g., 12345 or 123 45) to see success message.</p>
        </div>
      </div>
    `,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-check-postcode')).toBeTruthy();
  },
  decorators: [
    moduleMetadata({
      imports: [ReactiveFormsModule, StoryblokCheckPostcodeComponent],
      providers: [
        {
          provide: DeliveryService,
          useFactory: () => {
            const service = new MockDeliveryService();
            service.setMockResponse({ isDeliverable: true, errors: [] });
            (window as any).storybookDeliveryService = service;
            return service;
          }
        }
      ],
    }),
  ],
};

export const ErrorState: Story = {
  name: 'Error - Not Deliverable',
  render: () => ({
    template: `
      <div style="max-width: 600px; padding: 2rem;">
        <h3 style="margin-bottom: 1rem;">Error State</h3>
        <app-check-postcode />
        <div style="margin-top: 2rem; padding: 1rem; background: #ffebee; border-radius: 4px;">
          <p style="margin: 0;"><strong>Mock:</strong> Returns deliverable = false</p>
          <p style="margin: 0.5rem 0 0 0;">Enter a postcode with or without space (e.g., 12345 or 123 45) to see error message.</p>
        </div>
      </div>
    `,
  }),
  decorators: [
    moduleMetadata({
      imports: [ReactiveFormsModule, StoryblokCheckPostcodeComponent],
      providers: [
        {
          provide: DeliveryService,
          useFactory: () => {
            const service = new MockDeliveryService();
            service.setMockResponse({ isDeliverable: false, errors: ['Delivery not available'] });
            return service;
          }
        }
      ],
    }),
  ],
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-check-postcode')).toBeTruthy();
  },
};

export const NetworkError: Story = {
  name: 'Network Error',
  render: () => ({
    template: `
      <div style="max-width: 600px; padding: 2rem;">
        <h3 style="margin-bottom: 1rem;">Network Error State</h3>
        <app-check-postcode />
        <div style="margin-top: 2rem; padding: 1rem; background: #fff3e0; border-radius: 4px;">
          <p style="margin: 0;"><strong>Mock:</strong> Throws network error</p>
          <p style="margin: 0.5rem 0 0 0;">Enter a postcode with or without space (e.g., 12345 or 123 45) to see error handling.</p>
        </div>
      </div>
    `,
  }),
  decorators: [
    moduleMetadata({
      imports: [ReactiveFormsModule, StoryblokCheckPostcodeComponent],
      providers: [
        {
          provide: DeliveryService,
          useFactory: () => {
            const service = new MockDeliveryService();
            service.setShouldFail(true);
            return service;
          }
        }
      ],
    }),
  ],
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-check-postcode')).toBeTruthy();
  },
};

export const ValidationErrors: Story = {
  name: 'Validation Errors',
  render: () => ({
    template: `
      <div style="max-width: 600px; padding: 2rem;">
        <h3 style="margin-bottom: 1rem;">Form Validation</h3>
        <app-check-postcode />
        <div style="margin-top: 2rem; padding: 1rem; background: #f5f5f5; border-radius: 4px;">
          <h4 style="margin-top: 0;">Test Cases:</h4>
          <ul style="margin-bottom: 0;">
            <li>Enter incorrect format (e.g., "123", "1234") and submit</li>
            <li>Enter letters (field should only accept numbers and space)</li>
            <li>Try to submit empty form</li>
            <li>Valid formats: "12345" or "123 45"</li>
          </ul>
        </div>
      </div>
    `,
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-check-postcode')).toBeTruthy();
  },
};
